/**
 * Vehicle Icon Utilities
 * Shared functions for creating vehicle icons used in map markers and UI components
 */

/**
 * Create GPS marker icon (SVG) - Uses exact FontAwesome5 car icon
 * This function creates a marker with the same FontAwesome5 "car" icon used in the sidebar
 */
export const createGPSMarkerIcon = (
  isOnline: boolean, 
  speed: number = 0, 
  color: string = '#007AFF', 
  size: number = 48
): string => {
  // Use blue (#007AFF) for online, gray (#8E8E93) for offline
  const markerColor = isOnline ? color : '#8E8E93';
  const shadowColor = isOnline ? 'rgba(0, 122, 255, 0.3)' : 'rgba(142, 142, 147, 0.3)';
  const center = size / 2;
  const radius = size * 0.375; // 18px for 48px, scales proportionally
  const iconSize = size * 0.5; // 24px for 48px marker
  
  // Use exact FontAwesome Free v5.15.4 car SVG path (viewBox: 0 0 512 512)
  // FontAwesome5 car path from https://fontawesome.com/icons/car
  const carPath = 'M499.99 176h-59.87l-16.64-41.6C406.38 91.63 365.57 64 319.5 64h-127c-46.06 0-86.88 27.63-103.99 70.4L71.87 176H12.01C4.2 176-1.53 183.34.37 190.91l6 24C7.7 220.25 12.5 224 18.01 224h20.07C24.65 235.73 16 252.78 16 272v48c0 16.12 6.16 30.67 16 41.93V416c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32v-32h256v32c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32v-54.07c9.84-11.25 16-25.8 16-41.93v-48c0-19.22-8.65-36.27-22.07-48H494c5.51 0 10.31-3.75 11.64-9.09l6-24c1.89-7.57-3.84-14.91-11.65-14.91zm-352.06-17.83c7.29-18.22 24.94-30.17 44.57-30.17h127c19.63 0 37.28 11.95 44.57 30.17L384 208H128l19.93-49.83zM96 319.8c-19.2 0-32-12.76-32-31.9S76.8 256 96 256s48 28.71 48 47.85-28.8 15.95-48 15.95zm320 0c-19.2 0-48 3.19-48-15.95S396.8 256 416 256s32 12.76 32 31.9-12.8 31.9-32 31.9z';
  
  // Scale factor to fit icon inside circle (icon is 512x512, we want it to fit in ~24px area)
  const iconScale = (iconSize / 512) * 0.8; // Scale to 80% of available space for better fit
  
  const svg = `
    <svg width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">
      <!-- Shadow for depth -->
      <circle cx="${center}" cy="${center + 2}" r="${radius}" fill="${shadowColor}" opacity="0.4"/>
      
      <!-- Outer circle background -->
      <circle cx="${center}" cy="${center}" r="${radius}" fill="${markerColor}" stroke="white" stroke-width="${size * 0.052}"/>
      
      <!-- FontAwesome5 car icon centered - properly scaled -->
      <g transform="translate(${center}, ${center}) scale(${iconScale})">
        <path d="${carPath}" fill="white" transform="translate(-256, -256)"/>
      </g>
      
      <!-- Speed indicator badge (if moving) -->
      ${speed > 5 ? `
        <circle cx="${center + radius * 0.67}" cy="${center - radius * 0.67}" r="${size * 0.125}" fill="#FF9500" stroke="white" stroke-width="${size * 0.031}"/>
        <text x="${center + radius * 0.67}" y="${center - radius * 0.5}" font-family="Arial, sans-serif" font-size="${size * 0.146}" font-weight="bold" fill="white" text-anchor="middle">${Math.round(speed)}</text>
      ` : ''}
    </svg>
  `;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
};


